import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../firebase/config';
import { sendEmailVerification, reload } from 'firebase/auth';
import { useAuth } from '../firebase/AuthContext';
import './EmailVerification.css';

const EmailVerification = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // URLパラメータから遷移元の情報を取得
  const from = location.state?.from || '/top';
  const userEmail = user?.email || location.state?.email || '';

  useEffect(() => {
    // クールダウンタイマー
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  useEffect(() => {
    // 既に認証済みの場合は遷移
    if (user && user.emailVerified) {
      navigate(from);
    }
  }, [user, navigate, from]);

  const handleCheckVerification = async () => {
    setIsLoading(true);
    setMessage('');

    try {
      if (auth.currentUser) {
        // ユーザー情報を再読み込み
        await reload(auth.currentUser);
        
        if (auth.currentUser.emailVerified) {
          setIsVerified(true);
          setMessage('メール認証が完了しました！');
          
          // 少し待ってから遷移
          setTimeout(() => {
            navigate(from);
          }, 2000);
        } else {
          setMessage('まだメール認証が完了していません。メール内のリンクをクリックしてください。');
        }
      }
    } catch (error) {
      console.error('認証確認エラー:', error);
      setMessage('認証確認中にエラーが発生しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (resendCooldown > 0) return;

    setIsLoading(true);
    setMessage('');

    try {
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        setMessage('認証メールを再送信しました。');
        setResendCooldown(60); // 60秒のクールダウン
      }
    } catch (error) {
      console.error('メール再送信エラー:', error);
      setMessage('メール再送信中にエラーが発生しました。');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="email-verification-container">
      <div className="email-verification-box">
        <img src="/img/tiny-identity.png" alt="ロゴ" className="email-verification-logo" />
        <h1 className="email-verification-title">Tiny identity</h1>
        
        <div className="email-verification-content">
          <div className="email-verification-icon">
            <img src="/img/mail.png" alt="メールアイコン" />
          </div>
          
          <h2>認証メールを送信しました</h2>
          
          <p className="email-verification-description">
            以下のメールアドレスに認証メールを送信しました：
          </p>
          
          <div className="email-address">
            {userEmail}
          </div>
          
          <p className="email-verification-instruction">
            メール内の認証リンクをクリックして、アカウントを有効化してください。
            認証が完了したら、下のボタンをクリックしてください。
          </p>

          {message && (
            <div className={`message ${isVerified ? 'success' : 'info'}`}>
              {message}
            </div>
          )}

          <div className="email-verification-actions">
            <button 
              className="btn-primary"
              onClick={handleCheckVerification}
              disabled={isLoading}
            >
              {isLoading ? '確認中...' : '認証完了を確認'}
            </button>
            
            <button 
              className="btn-outline"
              onClick={handleResendEmail}
              disabled={isLoading || resendCooldown > 0}
            >
              {resendCooldown > 0 
                ? `再送信 (${resendCooldown}秒後)` 
                : 'メールを再送信'
              }
            </button>
            
            <button 
              className="btn-text"
              onClick={handleBackToLogin}
            >
              ログイン画面に戻る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
